import { useEffect, useId, useMemo, useState } from 'react';

import { Alert, Button, Group, Loader, Modal, Select, Stack, Text, Textarea } from '@mantine/core';

import {
  EMPTY_QUOTE,
  QuoteFields,
  quoteRequestFrom,
  type QuoteDraft,
} from '@/components/Modals/RegistryOrderQuoteFields';
import { formatRegistryMessageTime } from '@/components/RegistryOrders/RegistryOrdersCard/RegistryOrderMessages';
import { useRegistryOrderDetail } from '@/hooks/useRegistryOrderDetail';
import { RegistryOrderError } from '@/services/registry-order-error';
import {
  REGISTRY_ORDER_QUOTE_NOTE_MAX,
  type RegistryOrder,
  type RegistryOrderMessage,
  type RegistryOrderQuoteRecordRequest,
} from '@/types/registry-documents';

/** How much of a desk message names it in the "This quote answers" list. */
export const QUOTE_ANSWERS_EXCERPT_LENGTH = 60;

/** The choice of answering none of the desk's messages. A recorded
 * Message-ID is always bracketed (`<…>`), so none can read the same. */
const ANSWERS_NONE = 'none';

const FAILURE_TITLE = 'The quote was not recorded';

interface Failure {
  title: string;
  message: string;
  /** The order already has its date: no second attempt will take. */
  final: boolean;
}

/** What a refusal means to the person recording the quote. The API's
 * codes are turned into plain words; anything else is its own sentence. */
export function describeQuoteFailure(err: unknown): Failure {
  if (err instanceof RegistryOrderError) {
    if (err.kind === 'validation' && err.code === 'expected_at_set') {
      return {
        title: FAILURE_TITLE,
        message: 'The order already has an expected date.',
        final: true,
      };
    }
    if (err.kind === 'quote_not_allowed') {
      return {
        title: FAILURE_TITLE,
        message: 'Only a placed order that still owes documents can take a quote.',
        final: false,
      };
    }
    if (err.kind === 'validation' && err.code === 'unknown_message') {
      return {
        title: FAILURE_TITLE,
        message: 'The message chosen is no longer on the order. Choose another, or none of them.',
        final: false,
      };
    }
  }
  const message = err instanceof Error ? err.message : 'The request failed';
  return { title: FAILURE_TITLE, message, final: false };
}

/** Whether a quote recorded on its own may answer this message: one of
 * the desk's own, with a Message-ID to name, that brought no document and
 * that nobody has answered. */
function canAnswerWithQuote(message: RegistryOrderMessage): boolean {
  return (
    message.direction === 'inbound' &&
    message.messageId !== null &&
    message.newDocuments === 0 &&
    !message.answered
  );
}

/** A desk message as one line of the list: when it came, then the start
 * of its text. */
export function describeAnswerable(message: RegistryOrderMessage): string {
  const when = formatRegistryMessageTime(message.receivedAt) ?? 'Undated';
  const text = message.text.replace(/\s+/g, ' ').trim();
  if (text === '') {
    return `${when} · (no text kept)`;
  }
  const excerpt =
    text.length > QUOTE_ANSWERS_EXCERPT_LENGTH
      ? `${text.slice(0, QUOTE_ANSWERS_EXCERPT_LENGTH).trimEnd()}…`
      : text;
  return `${when} · ${excerpt}`;
}

/** What the dialog knows of the order's messages: unread yet (null), or
 * the list, with the error of a read that failed. */
export interface QuoteDialogMessages {
  items: RegistryOrderMessage[] | null;
  error: Error | null;
}

interface RegistryOrderQuoteDialogProps {
  opened: boolean;
  onClose: () => void;
  /** The order the quote is for. Null before the first one is chosen; kept
   * by the caller while the dialog closes, so its title does not blank. */
  order: Pick<RegistryOrder, 'id' | 'reference' | 'companyName'> | null;
  /** Throws on failure; the dialog stays open and shows the message. */
  onConfirm: (request: RegistryOrderQuoteRecordRequest) => Promise<unknown>;
  /** The order's messages, for the desk message the quote may answer. */
  messages: QuoteDialogMessages;
}

/**
 * The dialog itself, with the order's messages handed in: what the story
 * renders, and what `RegistryOrderQuoteModal` wraps with the read.
 */
export function RegistryOrderQuoteDialog({
  opened,
  onClose,
  order,
  onConfirm,
  messages,
}: Readonly<RegistryOrderQuoteDialogProps>) {
  const [quote, setQuote] = useState<QuoteDraft>(EMPTY_QUOTE);
  const [note, setNote] = useState('');
  // Null until a choice is made: the latest unanswered message is taken
  // as the one the quote answers, which is almost always right.
  const [answers, setAnswers] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  // Cleared on close, so reopening for another order never inherits a
  // half-typed quote, or the message chosen for another order.
  useEffect(() => {
    if (!opened) {
      setQuote(EMPTY_QUOTE);
      setNote('');
      setAnswers(null);
      setFailure(null);
    }
  }, [opened]);

  // Oldest first, as the API lists them; one option per Message-ID.
  const answerable = useMemo(() => {
    const seen = new Set<string>();
    return (messages.items ?? []).filter((message) => {
      if (!canAnswerWithQuote(message) || seen.has(message.messageId as string)) {
        return false;
      }
      seen.add(message.messageId as string);
      return true;
    });
  }, [messages.items]);
  const latest = answerable.length ? answerable[answerable.length - 1] : null;
  const answersChoice = answers ?? latest?.messageId ?? ANSWERS_NONE;
  const answersMessageId = answersChoice === ANSWERS_NONE ? null : answersChoice;
  const options = useMemo(
    () => [
      ...answerable.map((message) => ({
        value: message.messageId as string,
        label: describeAnswerable(message),
      })),
      { value: ANSWERS_NONE, label: 'None of them' },
    ],
    [answerable]
  );

  const request = quoteRequestFrom(quote);
  // Not before the messages are known: the quote would otherwise be
  // recorded against none, and the desk's message would keep counting.
  const messagesPending = messages.items === null && messages.error === null;
  const messagesUnread = messages.items === null && messages.error !== null;
  const unrecordable = failure?.final === true;
  const canSubmit = request !== null && !submitting && !messagesPending && !unrecordable;
  // The line that says why Record waits, or why no message is offered,
  // read out as the button's description: a disabled button says nothing
  // of itself to a screen reader, and the line is a live region so the
  // change from reading to read, or to failed, is announced.
  const messagesLineId = useId();
  const describedBy = messagesPending || messagesUnread ? messagesLineId : undefined;

  const submit = async () => {
    if (!request) {
      return;
    }
    setSubmitting(true);
    setFailure(null);
    try {
      await onConfirm({
        ...request,
        answers_message_id: answersMessageId,
        note: note.trim() || null,
      });
    } catch (err) {
      setFailure(describeQuoteFailure(err));
    } finally {
      setSubmitting(false);
    }
  };

  // A record in flight is not abandoned by dismissing the dialog: its
  // outcome, a refusal above all, must still have somewhere to be shown.
  const dismiss = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={dismiss}
      title={order ? `Record quote for ${order.reference}` : 'Record quote'}
    >
      <Stack>
        <Text size="sm" c="dimmed">
          For a quote accepted outside the Hub. Recording it sets the expected date so the order is
          chased if it is late.
        </Text>
        <QuoteFields draft={quote} onChange={setQuote} />
        {messagesPending && (
          <Group
            gap="xs"
            role="status"
            id={messagesLineId}
            data-testid="registry-order-quote-messages-loading"
          >
            <Loader size="xs" aria-hidden />
            <Text size="xs" c="dimmed">
              Reading the messages from the research desk…
            </Text>
          </Group>
        )}
        {messagesUnread && (
          <Text
            size="xs"
            c="dimmed"
            role="status"
            id={messagesLineId}
            data-testid="registry-order-quote-messages-error"
          >
            The messages from the research desk could not be read, so the quote is recorded without
            answering one of them.
          </Text>
        )}
        {answerable.length > 0 && (
          <Select
            label="This quote answers"
            description="The desk's message that offered it; it then reads as answered and stops counting as waiting."
            data={options}
            value={answersChoice}
            onChange={(value) => setAnswers(value ?? ANSWERS_NONE)}
            allowDeselect={false}
            comboboxProps={{ withinPortal: false }}
            data-testid="registry-order-quote-answers"
          />
        )}
        <Textarea
          label="Note"
          description="Optional"
          autosize
          minRows={2}
          // The API refuses a longer note.
          maxLength={REGISTRY_ORDER_QUOTE_NOTE_MAX}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          data-testid="registry-order-quote-note"
        />
        {failure && (
          <Alert
            color={failure.final ? 'orange' : 'red'}
            title={failure.title}
            data-testid="registry-order-quote-failure"
          >
            {failure.message}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={dismiss} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={!canSubmit}
            loading={submitting}
            aria-describedby={describedBy}
            data-testid="registry-order-quote-confirm"
          >
            Record quote
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

type RegistryOrderQuoteModalProps = Omit<RegistryOrderQuoteDialogProps, 'messages'>;

/**
 * Record the desk's quote on its own, for one accepted or received by
 * email outside the Hub. The price stands where the list price would, and
 * the turnaround gives the order the expected date that lets it be chased
 * when it is late. The desk's message that offered the quote can be named
 * as answered, so it stops counting as waiting for a reply.
 *
 * The order's messages are read here, while the dialog is open, rather
 * than handed in: the admin queue's rows carry none, and the report card
 * has them only while the row is opened. One place serves both surfaces.
 */
export function RegistryOrderQuoteModal({
  opened,
  onClose,
  order,
  onConfirm,
}: Readonly<RegistryOrderQuoteModalProps>) {
  const { detail, error } = useRegistryOrderDetail(opened && order ? order.id : null);
  // `detail` is the chosen order's own or undefined: the hook never hands
  // back another order's read.
  const messages = useMemo<QuoteDialogMessages>(
    () => ({ items: detail?.messages ?? null, error }),
    [detail, error]
  );

  return (
    <RegistryOrderQuoteDialog
      opened={opened}
      onClose={onClose}
      order={order}
      onConfirm={onConfirm}
      messages={messages}
    />
  );
}
